/**
 * FilterChip Component
 *
 * Dismissable chip for displaying a single selected filter value.
 * Label is free-form; only the X button dismisses the chip.
 */

// Animation
import { motion } from 'framer-motion';

// UI primitives
import { Button } from '@/components/ui/button';

// Icons
import { ChipCloseIcon } from '@icons/ChipCloseIcon';

export interface FilterChipProps {
  /** Human-readable value shown in the chip */
  label: string;
  /** Callback when the X button is clicked */
  onDismiss: () => void;
  /** Custom aria-label for the dismiss button. Defaults to `Remove ${label} filter`. */
  dismissAriaLabel?: string;
}

export function FilterChip({ label, onDismiss, dismissAriaLabel }: FilterChipProps) {
  return (
    <motion.div
      initial={false}
      exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
      whileTap={{ scale: 0.98 }}
      className="flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-lg border border-dinnerplate-white bg-dinnerplate-white-lighter antialiased select-none"
    >
      <span className="text-sm text-grey font-inter font-semibold leading-[1.5]">
        {label}
      </span>
      <Button
        variant="ghost"
        onClick={onDismiss}
        className="relative p-0 h-auto hover:bg-transparent hover:text-off-black before:content-[''] before:absolute before:inset-[-10px]"
        aria-label={dismissAriaLabel ?? `Remove ${label} filter`}
      >
        <ChipCloseIcon className="text-grey transition-colors" />
      </Button>
    </motion.div>
  );
}
